// MARCA | 01/06/2026 | New file – Bundle builder custom element with client-side bundle cart, card interception, modal, and submit-all logic

/**
 * @typedef {object} BundleItem
 * @property {string} variantId
 * @property {string} productId
 * @property {string} handle
 * @property {string} title
 * @property {number} price  - in cents
 * @property {string} image
 * @property {number} quantity
 * @property {boolean} available
 */

class BundleBuilderComponent extends HTMLElement {
  /** @type {Map<string, BundleItem>} keyed by variantId */
  #items = new Map();

  /** @type {{ threshold: number, label: string, ref: string }[]} */
  #milestones = [];

  /** @type {number[]} sorted thresholds in cents */
  #sortedThresholds = [];

  /** @type {number} */
  #segmentSize = 0;

  /** @type {string} */
  #moneyFormat = "£{{amount}}";

  /** @type {MutationObserver|null} */
  #gridObserver = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {Map<string, any>} product data cache */
  #productCache = new Map();

  /** @type {Map<string, any>} product metafield cache */
  #metaCache = new Map();

  // MARCA | 18/06/2026 | Mobile bar top-position state tracking
  /** @type {'top'|'bottom'} */
  #mobileBarPosition = "bottom";

  /** @type {IntersectionObserver|null} */
  #headingObserver = null;

  /** @type {MutationObserver|null} */
  #headerMutationObserver = null;

  /** @type {boolean} heading/drawer visible in viewport */
  #headingVisible = true;

  /** @type {boolean} sticky header currently shown */
  #headerActive = false;

  connectedCallback() {
    this.#moneyFormat = this.dataset.moneyFormat || "£{{amount}}";

    this.#milestones = [
      {
        threshold: parseFloat(this.dataset.m1Threshold || "0"),
        label: this.dataset.m1Label || "",
        ref: "milestone1",
      },
      {
        threshold: parseFloat(this.dataset.m2Threshold || "0"),
        label: this.dataset.m2Label || "",
        ref: "milestone2",
      },
      {
        threshold: parseFloat(this.dataset.m3Threshold || "0"),
        label: this.dataset.m3Label || "",
        ref: "milestone3",
      },
    ].filter((m) => m.threshold > 0);

    this.#sortedThresholds = this.#milestones
      .map((m) => m.threshold * 100)
      .sort((a, b) => a - b);
    this.#segmentSize =
      this.#milestones.length > 0 ? 100 / this.#milestones.length : 100;

    this.#restoreState();
    this.#bindEvents();
    this.#observeGrid();
    this.#renderMilestoneLabels();
    this.#updateUI();

    // MARCA | 18/06/2026 | Initialise top-position mobile bar visibility logic
    this.#mobileBarPosition = /** @type {'top'|'bottom'} */ (
      this.dataset.mobileBarPosition || "bottom"
    );
    if (this.#mobileBarPosition === "top") {
      this.#initMobileBarTopObservers();
    }

    requestAnimationFrame(() => this.#processExistingCards());
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#gridObserver?.disconnect();
    // MARCA | 18/06/2026 | Disconnect mobile bar top-position observers
    this.#headingObserver?.disconnect();
    this.#headerMutationObserver?.disconnect();
  }

  // ─── Refs helper ────────────────────────────────────────────────────
  /** @param {string} name @returns {HTMLElement|null} */
  #ref(name) {
    return this.querySelector(`[ref="${name}"]`);
  }

  // ─── Mobile bar top-position observers ─────────────────────────────
  // MARCA | 18/06/2026 | IntersectionObserver on heading + MutationObserver on header sticky-state to control top-bar visibility
  #initMobileBarTopObservers() {
    const heading = this.querySelector(".bundle-builder__heading");
    const drawer = this.querySelector(".bundle-builder__drawer");

    const observedVisibility = new Map();

    if (heading) {
      this.#headingObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            observedVisibility.set(entry.target, entry.isIntersecting);
          }
          this.#headingVisible = Array.from(observedVisibility.values()).some(
            Boolean,
          );
          this.#updateMobileBarVisibility();
        },
        { threshold: 0 },
      );
      this.#headingObserver.observe(heading);
      if (drawer) this.#headingObserver.observe(drawer);
    }

    const header = /** @type {HTMLElement|null} */ (
      document.querySelector("#header-component, [data-sticky-state]")
    );
    if (header) {
      const stickyMode = header.getAttribute("sticky");
      const isAlwaysSticky = stickyMode === "always";

      if (isAlwaysSticky) {
        this.#headerActive = true;
      } else {
        this.#headerActive = header.dataset.stickyState === "active";
      }

      this.#headerMutationObserver = new MutationObserver(() => {
        if (isAlwaysSticky) {
          this.#headerActive = true;
        } else {
          this.#headerActive = header.dataset.stickyState === "active";
        }
        this.#updateMobileBarVisibility();
      });

      this.#headerMutationObserver.observe(header, {
        attributes: true,
        attributeFilter: ["data-sticky-state"],
      });
    } else {
      this.#headerActive = true;
    }

    window.addEventListener("resize", () => this.#updateMobileBarVisibility(), {
      signal: this.#abortController.signal,
    });
    this.#updateMobileBarVisibility();
  }

  #updateMobileBarVisibility() {
    const mobileBar = this.#ref("mobileBar");
    if (!mobileBar || this.#mobileBarPosition !== "top") return;

    if (!this.#isMobile()) {
      mobileBar.classList.remove("is-visible");
      return;
    }

    const shouldShow = !this.#headingVisible && this.#headerActive;
    mobileBar.classList.toggle("is-visible", shouldShow);
  }

  // ─── State persistence ──────────────────────────────────────────────
  #saveState() {
    try {
      const data = Array.from(this.#items.entries());
      sessionStorage.setItem("bundle-builder-items", JSON.stringify(data));
    } catch {
      /* quota errors */
    }
  }

  #restoreState() {
    try {
      const raw = sessionStorage.getItem("bundle-builder-items");
      if (!raw) return;
      const data = JSON.parse(raw);
      if (Array.isArray(data)) {
        this.#items = new Map(data);
      }
    } catch {
      /* corrupt data */
    }
  }

  // ─── Event binding ──────────────────────────────────────────────────
  #bindEvents() {
    const signal = this.#abortController.signal;

    this.#ref("checkoutBtn")?.addEventListener(
      "click",
      () => this.#submitToCart(),
      { signal },
    );
    this.#ref("carouselPrev")?.addEventListener(
      "click",
      () => this.#scrollCarousel(-1),
      { signal },
    );
    this.#ref("carouselNext")?.addEventListener(
      "click",
      () => this.#scrollCarousel(1),
      { signal },
    );
    this.#ref("carouselTrack")?.addEventListener(
      "scroll",
      () => this.#updateCarouselArrows(),
      { signal, passive: true },
    );

    // MARCA | 02/06/2026 | Mobile bar tap scrolls to the bundle section top
    this.#ref("mobileBar")?.addEventListener(
      "click",
      () => this.#scrollToSection(),
      { signal },
    );

    // Modal controls
    this.#ref("modalClose")?.addEventListener(
      "click",
      () => this.#closeModal(),
      { signal },
    );
    this.#ref("productModal")?.addEventListener(
      "click",
      (e) => {
        if (e.target === this.#ref("productModal")) this.#closeModal();
      },
      { signal },
    );
    this.#ref("modalAtc")?.addEventListener(
      "click",
      () => this.#handleModalAddToBox(),
      { signal },
    );
    this.#ref("modalPrev")?.addEventListener(
      "click",
      () => this.#scrollModalGallery(-1),
      { signal },
    );
    this.#ref("modalNext")?.addEventListener(
      "click",
      () => this.#scrollModalGallery(1),
      { signal },
    );
    this.#ref("modalGalleryTrack")?.addEventListener(
      "scroll",
      () => this.#updateModalArrows(),
      { signal },
    );

    // MARCA | 02/06/2026 | Animated accordion for price breakdown
    this.#initBreakdownAccordion(signal);

    // Intercept clicks on the product grid (event delegation)
    document.addEventListener("click", (e) => this.#handleDocumentClick(e), {
      signal,
      capture: true,
    });

    // Intercept quick-add form submissions to redirect to bundle instead of cart
    document.addEventListener("submit", (e) => this.#handleFormSubmit(e), {
      signal,
      capture: true,
    });
  }

  // ─── Product grid observation ───────────────────────────────────────
  #getProductGrid() {
    return (
      document.querySelector("results-list .product-grid") ||
      document.querySelector(".product-grid")
    );
  }

  #observeGrid() {
    // Observe results-list (persists across filter changes) for any DOM mutations in the grid
    const resultsList =
      document.querySelector("results-list") ||
      document.querySelector(".product-grid-container");
    if (!resultsList) return;

    this.#gridObserver = new MutationObserver(() => {
      requestAnimationFrame(() => this.#processExistingCards());
    });

    this.#gridObserver.observe(resultsList, { childList: true, subtree: true });

    // Also re-process after filter updates which re-render the section HTML
    document.addEventListener(
      "filter:update",
      () => {
        setTimeout(() => this.#processExistingCards(), 600);
      },
      { signal: this.#abortController.signal },
    );
  }

  #processExistingCards() {
    const grid = this.#getProductGrid();
    if (!grid) return;
    const items = grid.querySelectorAll(".product-grid__item");
    items.forEach((item) =>
      this.#processCard(/** @type {HTMLElement} */ (item)),
    );
  }

  // MARCA | 02/06/2026 | Overlay appended inside card-gallery so pointer events stay within gallery bounds (fixes hover disappear)
  /** @param {HTMLElement} cardContainer */
  #processCard(cardContainer) {
    if (cardContainer.dataset.bundleProcessed) return;
    cardContainer.dataset.bundleProcessed = "true";

    const card = /** @type {HTMLElement|null} */ (
      cardContainer.querySelector("product-card")
    );
    if (!card) return;

    const gallery = card.querySelector(
      ".card-gallery, [data-view-transition-to-main-product]",
    );
    if (!gallery || gallery.querySelector(".bundle-builder-add-overlay"))
      return;

    const productId = card.dataset.productId;
    const link = card.querySelector("a.product-card__link");
    const handle = link ? this.#extractHandle(link.getAttribute("href")) : "";

    const btn = document.createElement("button");
    btn.className = "bundle-builder-add-overlay";
    btn.type = "button";
    btn.dataset.productId = productId || "";
    btn.dataset.handle = handle;
    btn.innerHTML = `
      <span class="bundle-builder-add-overlay__icon">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
      </span>
      <span class="bundle-builder-add-overlay__text">Add to Box</span>
    `;
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.#handleQuickAdd(handle, btn);
    });

    gallery.appendChild(btn);
  }

  // ─── Click interception ─────────────────────────────────────────────
  // MARCA | 02/06/2026 | Improved click interception – always let bundle overlay buttons through
  /** @param {MouseEvent} e */
  #handleDocumentClick(e) {
    const target = /** @type {HTMLElement} */ (e.target);

    // Never intercept clicks on our own overlay button or within the bundle builder section
    if (
      target.closest(".bundle-builder-add-overlay") ||
      target.closest(".bundle-builder")
    )
      return;

    const productCard = target.closest("product-card, product-card-link");
    if (!productCard) return;

    const grid = this.#getProductGrid();
    if (!grid || !grid.contains(productCard)) return;

    // Let any remaining interactive elements through (shouldn't be any now quick-add is hidden)
    if (target.closest('button, input, select, textarea, [role="button"]'))
      return;

    e.preventDefault();
    e.stopPropagation();

    const link = productCard.querySelector("a.product-card__link");
    const handle = link ? this.#extractHandle(link.getAttribute("href")) : "";
    if (handle) this.#openModal(handle);
  }

  /** @param {SubmitEvent} e */
  #handleFormSubmit(e) {
    const form = /** @type {HTMLFormElement} */ (e.target);
    if (!form || form.tagName !== "FORM") return;

    const grid = this.#getProductGrid();
    if (!grid || !grid.contains(form)) return;

    // Only intercept add-to-cart forms within the product grid
    const productCard = form.closest("product-card, product-card-link");
    if (!productCard) return;

    e.preventDefault();
    e.stopPropagation();

    const variantInput = /** @type {HTMLInputElement|null} */ (
      form.querySelector('input[name="id"]')
    );
    const variantId = variantInput?.value;
    if (!variantId) return;

    const link = productCard.querySelector("a.product-card__link");
    const handle = link ? this.#extractHandle(link.getAttribute("href")) : "";
    if (handle) {
      this.#fetchProduct(handle).then((product) => {
        if (!product) return;
        const variant =
          product.variants.find(
            (/** @type {any} */ v) => String(v.id) === variantId,
          ) || product.variants[0];
        this.#addItem({
          variantId: String(variant.id),
          productId: String(product.id),
          handle: product.handle,
          title: product.title,
          price: variant.price,
          image: product.images?.[0] || "",
          quantity: 1,
          available: variant.available,
        });
      });
    }
  }

  /** @param {string|null} url @returns {string} */
  #extractHandle(url) {
    if (!url) return "";
    const match = url.match(/\/products\/([^?#/]+)/);
    return match ? (match[1] ?? "") : "";
  }

  // ─── Quick add ──────────────────────────────────────────────────────
  /** @param {string} handle @param {HTMLButtonElement} btn */
  async #handleQuickAdd(handle, btn) {
    if (!handle) return;

    btn.classList.add("is-added");
    const textEl = btn.querySelector(".bundle-builder-add-overlay__text");
    if (textEl) textEl.textContent = "Added!";

    try {
      const product = await this.#fetchProduct(handle);
      if (!product) return;

      const variant =
        product.variants.find((/** @type {any} */ v) => v.available) ||
        product.variants[0];
      this.#addItem({
        variantId: String(variant.id),
        productId: String(product.id),
        handle: product.handle,
        title: product.title,
        price: variant.price,
        image: product.images?.[0] || "",
        quantity: 1,
        available: variant.available,
      });
    } catch (err) {
      console.error("Bundle: failed to quick-add", err);
    }

    setTimeout(() => {
      btn.classList.remove("is-added");
      if (textEl) textEl.textContent = "Add to Box";
    }, 1200);
  }

  // ─── Product modal ──────────────────────────────────────────────────
  // MARCA | 02/06/2026 | Modal with Loox reviews + short description from metafields via Section Rendering API
  /** @param {string} handle */
  async #openModal(handle) {
    const [product, meta] = await Promise.all([
      this.#fetchProduct(handle),
      this.#fetchProductMeta(handle),
    ]);
    if (!product) return;

    const modal = /** @type {HTMLDialogElement|null} */ (
      this.#ref("productModal")
    );
    if (!modal) return;

    const variant =
      product.variants.find((/** @type {any} */ v) => v.available) ||
      product.variants[0];

    // Reviews – clone Loox widget from the grid card, or build one with metafield data
    const reviewsEl = this.#ref("modalReviews");
    if (reviewsEl) {
      const gridCard = this.#findGridCard(product.id);
      const existingLoox = gridCard?.querySelector(
        '.loox-rating, [class*="loox"]',
      );
      if (existingLoox) {
        reviewsEl.innerHTML = "";
        reviewsEl.appendChild(existingLoox.cloneNode(true));
      } else {
        const rating = meta?.loox_avg_rating || "";
        const raters = meta?.loox_num_reviews || "";
        reviewsEl.innerHTML = `<div class="loox-rating" data-id="${product.id}" data-rating="${rating}" data-raters="${raters}"></div>`;
        this.#tryInitLoox();
      }
    }

    // Title
    const titleEl = this.#ref("modalTitle");
    if (titleEl) titleEl.textContent = product.title;

    // Description – prefer metafield short description, fall back to body_html excerpt
    const descEl = this.#ref("modalDescription");
    if (descEl) {
      if (meta?.description) {
        descEl.textContent = meta.description;
      } else {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = product.body_html || "";
        const text = (tempDiv.textContent || "").trim();
        descEl.textContent =
          text.length > 250
            ? text.substring(0, 250).replace(/\s+\S*$/, "") + "…"
            : text;
      }
    }

    // Price
    const priceEl = this.#ref("modalPrice");
    if (priceEl) priceEl.textContent = this.#formatMoney(variant.price);

    // Gallery
    const galleryTrack = this.#ref("modalGalleryTrack");
    if (galleryTrack) {
      galleryTrack.innerHTML = product.images
        .map(
          (/** @type {string} */ img) =>
            `<div class="bundle-builder__modal-gallery-slide"><img src="${img}" alt="${product.title}" loading="lazy" width="500"></div>`,
        )
        .join("");
      galleryTrack.scrollLeft = 0;
    }
    this.#updateModalArrows();

    // ATC button
    const atcBtn = /** @type {HTMLButtonElement|null} */ (
      this.#ref("modalAtc")
    );
    if (atcBtn) {
      atcBtn.dataset.variantId = String(variant.id);
      atcBtn.dataset.productHandle = handle;
      atcBtn.disabled = !variant.available;
      atcBtn.textContent = variant.available ? "Add to Box" : "Sold Out";
    }

    modal.showModal();
  }

  /** Locate a product card in the grid by product ID to extract rendered data (e.g. Loox reviews) */
  /** @param {number|string} productId @returns {HTMLElement|null} */
  #findGridCard(productId) {
    const grid = this.#getProductGrid();
    if (!grid) return null;
    return grid.querySelector(`product-card[data-product-id="${productId}"]`);
  }

  #closeModal() {
    const modal = /** @type {HTMLDialogElement|null} */ (
      this.#ref("productModal")
    );
    modal?.close();
  }

  #handleModalAddToBox() {
    const atcBtn = this.#ref("modalAtc");
    if (!atcBtn) return;

    const variantId = atcBtn.dataset.variantId;
    const handle = atcBtn.dataset.productHandle;
    if (!variantId || !handle) return;

    const product = this.#productCache.get(handle);
    if (!product) return;

    const variant =
      product.variants.find(
        (/** @type {any} */ v) => String(v.id) === variantId,
      ) || product.variants[0];

    this.#addItem({
      variantId: String(variant.id),
      productId: String(product.id),
      handle: product.handle,
      title: product.title,
      price: variant.price,
      image: product.images?.[0] || "",
      quantity: 1,
      available: variant.available,
    });

    atcBtn.textContent = "Added!";
    setTimeout(() => {
      atcBtn.textContent = "Add to Box";
      this.#closeModal();
    }, 800);
  }

  /** @param {number} direction */
  #scrollModalGallery(direction) {
    const track = this.#ref("modalGalleryTrack");
    if (!track) return;
    const slide = /** @type {HTMLElement|null} */ (
      track.querySelector(".bundle-builder__modal-gallery-slide")
    );
    if (slide)
      track.scrollBy({
        left: slide.offsetWidth * direction,
        behavior: "smooth",
      });
  }

  #updateModalArrows() {
    const track = this.#ref("modalGalleryTrack");
    const prevBtn = this.#ref("modalPrev");
    const nextBtn = this.#ref("modalNext");
    if (!track || !prevBtn || !nextBtn) return;

    prevBtn.classList.toggle("is-hidden", track.scrollLeft <= 1);
    nextBtn.classList.toggle(
      "is-hidden",
      track.scrollLeft + track.offsetWidth >= track.scrollWidth - 1,
    );
  }

  #tryInitLoox() {
    const loox = /** @type {any} */ (window).loox;
    if (loox?.init) {
      try {
        loox.init();
      } catch {
        /* Loox not ready */
      }
    }
  }

  // ─── Scroll helpers ──────────────────────────────────────────────────
  // MARCA | 02/06/2026 | Scroll to bundle section top (used by mobile bar tap)
  #scrollToSection() {
    this.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // MARCA | 18/06/2026 | Scroll to the very top of the page (used after adding a product)
  #scrollToPageTop() {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  /** @returns {boolean} */
  #isMobile() {
    return window.innerWidth < 750;
  }

  // ─── Animated accordion ─────────────────────────────────────────────
  // MARCA | 02/06/2026 | Smooth height animation for the price breakdown <details>
  /** @param {AbortSignal} signal */
  #initBreakdownAccordion(signal) {
    const details = /** @type {HTMLDetailsElement|null} */ (
      this.#ref("breakdownDetails")
    );
    if (!details) return;

    /** @type {Animation|null} */
    let animation = null;

    const summary = details.querySelector("summary");
    if (!summary) return;

    summary.addEventListener(
      "click",
      (e) => {
        if (!this.#isMobile()) return;
        e.preventDefault();
        const content = /** @type {HTMLElement|null} */ (
          details.querySelector(".bundle-builder__breakdown-content")
        );
        if (!content) return;

        if (animation) animation.cancel();

        const isOpen = details.open;

        if (isOpen) {
          const startHeight = `${details.offsetHeight}px`;
          details.style.overflow = "hidden";
          const contentHeight = content.offsetHeight;
          const endHeight = `${details.offsetHeight - contentHeight}px`;

          animation = details.animate(
            { height: [startHeight, endHeight] },
            { duration: 250, easing: "ease" },
          );
          animation.onfinish = () => {
            details.open = false;
            details.style.overflow = "";
            details.style.height = "";
            animation = null;
          };
          animation.oncancel = () => {
            details.style.overflow = "";
            details.style.height = "";
            animation = null;
          };
        } else {
          details.open = true;
          const endHeight = `${details.offsetHeight}px`;
          const contentHeight = content.offsetHeight;
          const startHeight = `${details.offsetHeight - contentHeight}px`;
          details.style.overflow = "hidden";

          animation = details.animate(
            { height: [startHeight, endHeight] },
            { duration: 250, easing: "ease" },
          );
          animation.onfinish = () => {
            details.style.overflow = "";
            details.style.height = "";
            animation = null;
          };
          animation.oncancel = () => {
            details.style.overflow = "";
            details.style.height = "";
            animation = null;
          };
        }
      },
      { signal },
    );
  }

  // ─── Bundle state management ────────────────────────────────────────
  /** @param {BundleItem} item */
  #addItem(item) {
    const existing = this.#items.get(item.variantId);
    if (existing) {
      existing.quantity += 1;
    } else {
      this.#items.set(item.variantId, { ...item });
    }
    this.#saveState();
    this.#updateUI();

    // MARCA | 18/06/2026 | Scroll to page top on both mobile and desktop so the user sees their updated bundle
    this.#scrollToPageTop();
  }

  // MARCA | 02/06/2026 | Remove one unit at a time; only delete entry when quantity reaches zero
  /** @param {string} variantId */
  #removeItem(variantId) {
    const item = this.#items.get(variantId);
    if (item && item.quantity > 1) {
      item.quantity -= 1;
    } else {
      this.#items.delete(variantId);
    }
    this.#saveState();
    this.#updateUI();
  }

  /** @returns {number} total in cents */
  #getTotal() {
    let total = 0;
    for (const item of this.#items.values()) {
      total += item.price * item.quantity;
    }
    return total;
  }

  /** @returns {number} */
  #getItemCount() {
    let count = 0;
    for (const item of this.#items.values()) {
      count += item.quantity;
    }
    return count;
  }

  // ─── UI updates ─────────────────────────────────────────────────────
  #updateUI() {
    this.#renderCarousel();
    this.#updateProgressBar();
    this.#updatePricingPanel();
    this.#updateMobileBar();
    this.#updateButtons();
  }

  // MARCA | 02/06/2026 | Each unit rendered as a separate slot (no quantity badges)
  #renderCarousel() {
    const track = this.#ref("carouselTrack");
    if (!track) return;

    const items = Array.from(this.#items.values());
    let html = "";
    let totalSlots = 0;

    for (const item of items) {
      for (let q = 0; q < item.quantity; q++) {
        html += `
          <div class="bundle-builder__carousel-item" data-variant-id="${item.variantId}">
            <div class="bundle-builder__carousel-thumb">
              <button class="bundle-builder__carousel-remove" data-variant-id="${item.variantId}" aria-label="Remove ${item.title}">×</button>
              <img src="${item.image ? item.image.replace(/(\.\w+)(\?|$)/, "$1?width=240$2") : ""}" alt="${item.title}" width="120" height="120" loading="lazy">
            </div>
            <span class="bundle-builder__carousel-name">${item.title}</span>
          </div>
        `;
        totalSlots++;
      }
    }

    const emptySlots = Math.max(0, 8 - totalSlots);
    for (let i = 0; i < emptySlots; i++) {
      html += `<div class="bundle-builder__carousel-slot"></div>`;
    }

    track.innerHTML = html;

    track
      .querySelectorAll(".bundle-builder__carousel-remove")
      .forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          const vid = /** @type {HTMLElement} */ (btn).dataset.variantId;
          if (vid) this.#removeItem(vid);
        });
      });

    requestAnimationFrame(() => this.#updateCarouselArrows());
  }

  // MARCA | 02/06/2026 | Bidirectional carousel scroll with arrow visibility based on scroll position
  /** @param {number} direction  1 = right, -1 = left */
  #scrollCarousel(direction = 1) {
    const track = this.#ref("carouselTrack");
    if (track) track.scrollBy({ left: 264 * direction, behavior: "smooth" });
  }

  #updateCarouselArrows() {
    const track = this.#ref("carouselTrack");
    if (!track) return;
    const scrollable = track.scrollWidth > track.clientWidth;
    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
    const prevBtn = this.#ref("carouselPrev");
    const nextBtn = this.#ref("carouselNext");
    if (prevBtn) prevBtn.hidden = !scrollable || atStart;
    if (nextBtn) nextBtn.hidden = !scrollable || atEnd;
  }

  #updateProgressBar() {
    const totalCents = this.#getTotal();
    const totalValue = totalCents / 100;
    const fillPct = this.#calculateFill(totalCents);

    const fill = this.#ref("progressFill");
    if (fill) fill.style.width = `${fillPct}%`;

    for (const m of this.#milestones) {
      const el = this.#ref(m.ref);
      if (!el) continue;
      el.classList.toggle("is-unlocked", totalValue >= m.threshold);
    }

    this.#updateMilestoneLabels(totalValue);
  }

  /** @param {number} cartCents @returns {number} */
  #calculateFill(cartCents) {
    const thresholds = this.#sortedThresholds;
    if (thresholds.length === 0) return 0;

    let completedSegments = 0;
    for (let i = 0; i < thresholds.length; i++) {
      const current = thresholds[i] ?? 0;
      if (cartCents >= current) {
        completedSegments++;
      } else {
        const prev = i === 0 ? 0 : (thresholds[i - 1] ?? 0);
        const range = current - prev;
        const progress = cartCents - prev;
        const fraction = range > 0 ? progress / range : 0;
        return Math.min(
          100,
          completedSegments * this.#segmentSize + fraction * this.#segmentSize,
        );
      }
    }
    return 100;
  }

  #renderMilestoneLabels() {
    const container = this.#ref("milestoneLabels");
    if (!container) return;

    const sorted = [...this.#milestones].sort(
      (a, b) => a.threshold - b.threshold,
    );
    container.innerHTML = sorted
      .map(
        (m) =>
          `<span class="bundle-builder__milestone-label" data-threshold="${m.threshold}">${m.label}</span>`,
      )
      .join("");
  }

  /** @param {number} totalValue in pounds */
  #updateMilestoneLabels(totalValue) {
    const container = this.#ref("milestoneLabels");
    if (!container) return;

    const sorted = [...this.#milestones].sort(
      (a, b) => a.threshold - b.threshold,
    );
    const labels = container.querySelectorAll(
      ".bundle-builder__milestone-label",
    );

    labels.forEach((label, i) => {
      const m = sorted[i];
      if (!m) return;

      const isUnlocked = totalValue >= m.threshold;
      label.classList.toggle("is-unlocked", isUnlocked);

      if (isUnlocked) {
        label.textContent = `${m.label} UNLOCKED`;
      } else {
        const remaining = m.threshold - totalValue;
        label.textContent = `Spend ${this.#formatMoney(remaining * 100)} more for ${m.label}`;
      }
    });
  }

  #updatePricingPanel() {
    const total = this.#getTotal();
    const totalValue = total / 100;

    const standardEl = this.#ref("standardPrice");
    if (standardEl) standardEl.textContent = this.#formatMoney(total);

    const rewardsEl = this.#ref("unlockedRewards");
    if (rewardsEl) {
      const sorted = [...this.#milestones].sort(
        (a, b) => a.threshold - b.threshold,
      );
      const unlocked = sorted.filter((m) => totalValue >= m.threshold);

      if (unlocked.length > 0) {
        let html = '<p class="bundle-builder__reward-title">Discounts</p>';
        for (const m of unlocked) {
          html += `<div class="bundle-builder__reward-item"><span>${m.label}</span><span>✓</span></div>`;
        }
        rewardsEl.innerHTML = html;
      } else {
        rewardsEl.innerHTML = "";
      }
    }

    const bundleRow = this.#ref("bundlePriceRow");
    if (bundleRow) bundleRow.hidden = total === 0;

    const bundlePrice = this.#ref("bundlePrice");
    if (bundlePrice) bundlePrice.textContent = this.#formatMoney(total);
  }

  // MARCA | 02/06/2026 | Mobile bar shows price + product thumbnails + empty slots
  #updateMobileBar() {
    const itemsEl = this.#ref("mobileItems");
    if (!itemsEl) return;

    const items = Array.from(this.#items.values());
    let html = "";
    let totalSlots = 0;

    for (const item of items) {
      for (let q = 0; q < item.quantity; q++) {
        html += `
          <div class="bundle-builder__mobile-thumb">
            <img src="${item.image ? item.image.replace(/(\.\w+)(\?|$)/, "$1?width=100$2") : ""}" alt="${item.title}" width="44" height="44" loading="lazy">
          </div>
        `;
        totalSlots++;
      }
    }

    const emptySlots = Math.max(0, 4 - totalSlots);
    for (let i = 0; i < emptySlots; i++) {
      html += `<div class="bundle-builder__mobile-slot"></div>`;
    }

    itemsEl.innerHTML = html;

    const countEl = this.#ref("mobileCount");
    const count = this.#getItemCount();
    if (countEl) countEl.textContent = `${count} item${count !== 1 ? "s" : ""}`;

    const priceEl = this.#ref("mobilePrice");
    if (priceEl) priceEl.textContent = this.#formatMoney(this.#getTotal());
  }

  #updateButtons() {
    const hasItems = this.#items.size > 0;
    const checkoutBtn = /** @type {HTMLButtonElement|null} */ (
      this.#ref("checkoutBtn")
    );
    if (checkoutBtn) checkoutBtn.disabled = !hasItems;
  }

  // ─── Submit to cart ─────────────────────────────────────────────────
  async #submitToCart() {
    if (this.#items.size === 0) return;

    const checkoutBtn = /** @type {HTMLButtonElement|null} */ (
      this.#ref("checkoutBtn")
    );
    const origText = checkoutBtn?.textContent;

    if (checkoutBtn) {
      checkoutBtn.disabled = true;
      checkoutBtn.textContent = "Adding…";
    }

    const items = Array.from(this.#items.values()).map((item) => ({
      id: parseInt(item.variantId, 10),
      quantity: item.quantity,
    }));

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) throw new Error("Cart add failed");

      this.#items.clear();
      this.#saveState();
      this.#updateUI();

      if (checkoutBtn) checkoutBtn.textContent = "Added!";

      document.dispatchEvent(
        new CustomEvent("cart:update", {
          bubbles: true,
          detail: { sourceId: "bundle-builder", data: {} },
        }),
      );

      const cartDrawer = document.querySelector("cart-drawer-component");
      if (
        cartDrawer &&
        typeof (/** @type {any} */ (cartDrawer).open) === "function"
      ) {
        /** @type {any} */ (cartDrawer).open();
      }

      setTimeout(() => {
        if (checkoutBtn) {
          checkoutBtn.textContent = origText || "Checkout";
          checkoutBtn.disabled = true;
        }
      }, 2000);
    } catch (err) {
      console.error("Bundle: submit failed", err);
      if (checkoutBtn) {
        checkoutBtn.textContent = origText || "Checkout";
        checkoutBtn.disabled = false;
      }
    }
  }

  // ─── Product fetch ──────────────────────────────────────────────────
  /** @param {string} handle @returns {Promise<any|null>} */
  async #fetchProduct(handle) {
    if (this.#productCache.has(handle)) return this.#productCache.get(handle);

    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      const product = await res.json();

      // Normalise image URLs – the JS API may return protocol-relative or CDN paths
      if (Array.isArray(product.images)) {
        product.images = product.images.map((/** @type {any} */ img) => {
          if (typeof img === "string" && img.startsWith("//"))
            return "https:" + img;
          return typeof img === "string" ? img : "";
        });
      }

      this.#productCache.set(handle, product);
      return product;
    } catch {
      return null;
    }
  }

  // ─── Product meta fetch (Section Rendering API) ────────────────────
  // MARCA | 02/06/2026 | Fetch metafield data (short description, Loox) via Section Rendering API
  /** @param {string} handle @returns {Promise<{description: string|null, loox_avg_rating: string|null, loox_num_reviews: string|null}|null>} */
  async #fetchProductMeta(handle) {
    if (this.#metaCache.has(handle)) return this.#metaCache.get(handle) || null;
    try {
      const res = await fetch(
        `/products/${handle}?sections=bundle-product-meta`,
      );
      if (!res.ok) {
        this.#metaCache.set(handle, null);
        return null;
      }
      const sections = await res.json();
      const html = sections["bundle-product-meta"];
      if (!html) {
        this.#metaCache.set(handle, null);
        return null;
      }
      const doc = new DOMParser().parseFromString(html, "text/html");
      const script = doc.querySelector("[data-bundle-product-meta]");
      if (!script) {
        this.#metaCache.set(handle, null);
        return null;
      }
      const data = JSON.parse(script.textContent);
      this.#metaCache.set(handle, data);
      return data;
    } catch {
      this.#metaCache.set(handle, null);
      return null;
    }
  }

  // ─── Money formatting ───────────────────────────────────────────────
  /** @param {number} cents @returns {string} */
  #formatMoney(cents) {
    const shopify = /** @type {any} */ (window).Shopify;
    if (shopify?.formatMoney) {
      return shopify.formatMoney(cents, this.#moneyFormat);
    }
    return `£${(cents / 100).toFixed(2)}`;
  }
}

if (!customElements.get('bundle-builder-component')) {
  customElements.define('bundle-builder-component', BundleBuilderComponent);
}
